import { useMemo, useState, useCallback } from "react";
import { scaleLinear } from "d3";
import { dataVizLayout } from "@atlas-int/tokens";
const defaultChartMargin = dataVizLayout.defaultChartMargin;
import type { UseChartScalesProps } from "./types";

export const useChartScales = ({
  yearMin,
  yearMax,
  chartWidth,
  chartHeight,
  chartMargin = defaultChartMargin,
}: UseChartScalesProps) => {
  const [yAxisExtentDomain, setYAxisExtentDomain] = useState<
    { min: number; max: number } | undefined
  >(undefined);

  const setYAxisDomain = useCallback(
    (domainToSet: { min: number; max: number }) => {
      if (
        !yAxisExtentDomain ||
        (yAxisExtentDomain &&
          (domainToSet.min !== yAxisExtentDomain.min ||
            domainToSet.max !== yAxisExtentDomain.max))
      ) {
        setYAxisExtentDomain({ min: domainToSet.min, max: domainToSet.max });
      }
    },
    [yAxisExtentDomain],
  );

  /*
    In the scale generator below for xScale, note that we are using d3.scaleLinear().
    Another candidate scale generator that would be more semantically aligned with the
    variable represented on the x-axis is d3.scaleTime(). However, the x-axis variable
    is encoded as discrete years, and so to use d3.scaleTime(), all of the `year` values
    would need to be coerced into JS Date() objects. This would introduce a number of
    unnecessary code acrobatics, as well as the need to manage the oddities of JS Date()
    representations, e.g., representations across time zones. Thus, the choice here is
    to use d3.scaleLinear() and encode `year` as a continuous quantitative variable.
    */

  const xScale = useMemo(() => {
    return scaleLinear()
      .domain([yearMin, yearMax])
      .range([chartMargin.left, chartWidth - chartMargin.right]);
  }, [yearMin, yearMax, chartWidth, chartMargin.left, chartMargin.right]);

  const yScale = useMemo(() => {
    const scale = scaleLinear().range([
      chartHeight - chartMargin.bottom,
      chartMargin.top,
    ]);

    if (yAxisExtentDomain) {
      scale.domain([0, yAxisExtentDomain.max]).nice();
    }

    return scale;
  }, [chartHeight, yAxisExtentDomain, chartMargin.bottom, chartMargin.top]);

  return {
    xScale,
    yScale,
    setYAxisDomain,
  };
};
